import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Instagram, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function Contatti() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, subject: t("contact.subjectDefault") }));
  }, [t]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errorRequired"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.successMsg"));
      setForm({ name: "", email: "", phone: "", subject: t("contact.subjectDefault"), message: "" });
    } catch {
      toast.error(t("contact.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contatti" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="label-eyebrow mb-4">{t("contact.eyebrow")}</div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#2C2F2A]">
          {t("contact.title1")} <em className="text-[#8A9A86] font-light">{t("contact.title2")}</em>.
        </h1>
        <p className="mt-8 text-lg text-[#5C6356] leading-relaxed">{t("contact.intro")}</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 mt-16">
        <div className="lg:col-span-5 space-y-5">
          <div className="card-soft p-7">
            <Mail className="w-5 h-5 text-[#8A9A86] mb-3" strokeWidth={1.5} />
            <div className="label-eyebrow mb-1">Email</div>
            <a href="mailto:valeriacantnaturopatia@gmail.com" data-testid="contact-email" className="font-serif text-xl break-all hover:text-[#8A9A86] transition-colors">
              valeriacantnaturopatia@gmail.com
            </a>
          </div>
          <div className="card-soft p-7">
            <Phone className="w-5 h-5 text-[#8A9A86] mb-3" strokeWidth={1.5} />
            <div className="label-eyebrow mb-1">{t("contact.phone")}</div>
            <a href="tel:+393482478661" data-testid="contact-phone" className="font-serif text-xl hover:text-[#8A9A86] transition-colors">+39 348 247 8661</a>
          </div>
          <div className="card-soft p-7">
            <MapPin className="w-5 h-5 text-[#8A9A86] mb-3" strokeWidth={1.5} />
            <div className="label-eyebrow mb-1">{t("contact.studio")}</div>
            <a
              href="https://maps.app.goo.gl/Wzhg9jPpaPa8TEPb7"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address"
              className="font-serif text-xl hover:text-[#8A9A86] transition-colors"
            >
              Corso Italia, 259<br />74121 Taranto (TA)
            </a>
            <p className="text-sm text-[#5C6356] mt-3">{t("contact.hours")}</p>
          </div>
          <div className="flex gap-3">
            <a href="https://www.instagram.com/lanaturacomecura" target="_blank" rel="noreferrer" data-testid="contact-instagram"
              className="flex-1 card-soft p-5 flex items-center justify-center gap-2 hover:bg-[#8A9A86] hover:text-white hover:border-[#8A9A86] transition-colors">
              <Instagram size={18} strokeWidth={1.5} /> Instagram
            </a>
            <a href="https://maps.app.goo.gl/Wzhg9jPpaPa8TEPb7" target="_blank" rel="noreferrer" data-testid="contact-maps"
              className="flex-1 card-soft p-5 flex items-center justify-center gap-2 hover:bg-[#8A9A86] hover:text-white hover:border-[#8A9A86] transition-colors">
              <MapPin size={18} strokeWidth={1.5} /> Maps
            </a>
            <a href="https://wa.me/393482478661" target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="flex-1 card-soft p-5 flex items-center justify-center gap-2 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors">
              WhatsApp
            </a>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 card-soft p-8 sm:p-10 space-y-5">
          <h3 className="font-serif text-3xl">{t("contact.formTitle")}</h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name" className="label-eyebrow">{t("contact.name")} *</Label>
              <Input id="c-name" data-testid="contact-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5" />
            </div>
            <div>
              <Label htmlFor="c-email" className="label-eyebrow">{t("contact.email")} *</Label>
              <Input id="c-email" type="email" data-testid="contact-email-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-phone" className="label-eyebrow">{t("contact.phone")}</Label>
              <Input id="c-phone" data-testid="contact-phone-input" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5" placeholder="+39 ..." />
            </div>
            <div>
              <Label htmlFor="c-subject" className="label-eyebrow">{t("contact.subject")}</Label>
              <Input id="c-subject" data-testid="contact-subject" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-2 rounded-full border-[#E6E2D8] h-11 px-5" />
            </div>
          </div>

          <div>
            <Label htmlFor="c-message" className="label-eyebrow">{t("contact.message")} *</Label>
            <Textarea id="c-message" data-testid="contact-message" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-2 rounded-2xl border-[#E6E2D8] min-h-[160px] px-5 py-3"
              placeholder={t("contact.messagePlaceholder")} />
          </div>

          <button type="submit" data-testid="contact-submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
            <Send size={16} strokeWidth={1.5} /> {loading ? t("contact.sending") : t("contact.send")}
          </button>
        </form>
      </div>

      {/* Google Map */}
      <div className="mt-20">
        <div className="label-eyebrow mb-4">{t("contact.mapEyebrow")}</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2C2F2A] mb-8">{t("contact.mapTitle")}</h2>
        <div className="rounded-[2rem] overflow-hidden border border-[#E6E2D8]">
          <iframe
            title="Mappa studio Valeria Cantore"
            data-testid="contact-map"
            src="https://www.google.com/maps?q=Corso+Italia+259,+74121+Taranto+TA,+Italy&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}
